import React, { useRef, useState, useEffect } from 'react';

const AudioStream = ({
  text,
  repetitionPenalty = 1.5,
  temperature = 0.3,
  topP = 0.8,
}) => {
  const audioRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressBarRef = useRef(null);

  // Create audio element on component mount
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      
      // Set up event listeners
      audioRef.current.onloadeddata = () => {
        setIsLoaded(true);
      };
      
      audioRef.current.oncanplay = () => {
        audioRef.current.play();
        setIsPlaying(true);
      };
      
      audioRef.current.ontimeupdate = handleTimeUpdate;
      audioRef.current.onended = handleEnded;
    }
    
    // Clean up on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  const handleGenerate = () => {
    if (audioRef.current) {
      // Reset states
      setIsLoaded(false);
      setIsPlaying(false);
      setProgress(0);
      
      // Set the audio source to the TTS endpoint
      audioRef.current.src = `https://331xh3y7z1d4m1-8080.proxy.runpod.net/tts?prompt=${encodeURIComponent(text)}&repetition_penalty=${repetitionPenalty}&temperature=${temperature}&top_p=${topP}`;
      audioRef.current.load();
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current || !isLoaded) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const currentProgress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  };

  const handleProgressBarClick = (e) => {
    if (!audioRef.current || !isLoaded || !progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const seekPosition = position * 100;
    
    audioRef.current.currentTime = (seekPosition / 100) * audioRef.current.duration;
    setProgress(seekPosition);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };
  
  // Function to handle opening audio in a new tab
  const handleOpenInNewTab = () => {
    if (!audioRef.current || !audioRef.current.src) return;
    
    // Open the audio URL in a new tab
    window.open(audioRef.current.src, '_blank');
  };

  return (
    <div className="w-full p-2">
      <div className="flex items-center justify-between">
        {/* Conditional rendering for play button and progress bar */}
        {isLoaded ? (
          <>
            {/* Play/Pause Button with circle - only shown when audio is loaded */}
            <button 
              onClick={handlePlayPause}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white hover:bg-gray-800 hover:scale-110 active:scale-95 transition-all duration-150 flex-shrink-0"
            >
              {isPlaying ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <polygon points="5,3 19,12 5,21" />
                </svg>
              )}
            </button>
            
            {/* Custom Progress Bar - only shown when audio is loaded */}
            <div 
              className="mx-3 flex-grow h-8 flex items-center relative"
              onClick={handleProgressBarClick}
              ref={progressBarRef}
            >
              {/* Background track */}
              <div className="w-full bg-gray-200 h-1 rounded-sm absolute">
                {/* Progress indicator */}
                <div 
                  className="bg-black h-1 rounded-sm" 
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            
            {/* Open in New Tab Button */}
            <button
              onClick={handleOpenInNewTab}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white hover:bg-gray-800 hover:scale-110 active:scale-95 transition-all duration-150 flex-shrink-0 mr-3"
              title="Open in new tab"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 19H5V5h7V3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
              </svg>
            </button>
          </>
        ) : (
          <div className="flex-grow"></div> 
        )}
      </div>
    </div>
  );
};

export default AudioStream;